<!--
  ResetPassword.svelte — Set a new password using a reset code.

  Reads `email` and optionally `code` from the URL query string
  (e.g. `#/reset-password?email=a@b.com&code=123456`).

  Step 1: Enter the 6-digit reset code (pre-filled if in URL).
  Step 2: Enter + confirm a new password.
  Step 3: Submit → server validates code + sets new password.
-->
<script lang="ts">
	import { run, preventDefault } from 'svelte/legacy';

	import { Button } from 'flowbite-svelte';
	import { querystring } from '../../abstract-spa-router';
	import AuthLayout from './AuthLayout.svelte';
	import {
		FormField,
		PasswordField,
		AlertMessage,
		OTPInput,
		LoadingOverlay,
		RoutedButton,
		RoutedLink
	} from '../../components';
	import { resetPassword, validateResetCode } from '../../AppNetwork';
	import { t, t2 } from '../../i18n';

	// ── URL params ────────────────────────────────────────────

	/** Parse query parameters from the hash router. */
	function parseQuery(qs: string | null | undefined): URLSearchParams {
		return new URLSearchParams(qs || '');
	}


	// ── State ─────────────────────────────────────────────────

	/** Email address (from URL or entered manually). */
	let email = $state('');
	/** 6-digit reset code. */
	let code = $state('');
	/** Whether the code has been validated. */
	let codeValidated = $state(false);
	/** New password. */
	let newPassword = $state('');
	/** Confirm new password. */
	let confirmPassword = $state('');
	/** Whether the reset completed successfully. */
	let success = $state(false);
	/** Whether a network request is in progress. */
	let loading = $state(false);
	/** Top-level error message. */
	let errorMessage = $state('');

	// ── Field errors ──────────────────────────────────────────

	let emailError = $state('');
	let passwordError = $state('');
	let confirmPasswordError = $state('');


	/** Auto-validate code from URL params on page load. */
	async function autoValidate(): Promise<void> {
		loading = true;
		/** Validate the code from URL and surface any errors. */
		try {
			await validateResetCode(codeFromUrl);
			codeValidated = true;
		} catch (err: any) {
			errorMessage = err?.message || t2('Reset code is invalid or expired.');
		} finally {
			loading = false;
		}
	}

	// ── Validation ────────────────────────────────────────────

	/**
	 *
	 */
	function clearErrors(): void {
		emailError = '';
		passwordError = '';
		confirmPasswordError = '';
		errorMessage = '';
	}

	/**
	 *
	 */
	function validatePasswordForm(): boolean {
		clearErrors();
		let valid = true;

		/** Require a non-empty password of sufficient length. */
		if (!newPassword) {
			passwordError = t2('New password is required.');
			valid = false;
		} else if (newPassword.length < 8) {
			passwordError = t2('Password must be at least 8 characters.');
			valid = false;
		}

		/** Ensure the confirmation field matches the new password. */
		if (newPassword !== confirmPassword) {
			confirmPasswordError = t2('Passwords do not match.');
			valid = false;
		}

		return valid;
	}

	// ── Handlers ──────────────────────────────────────────────

	/**
	 * OTP input completed — validate the code with the server.
	 */
	async function handleCodeComplete(otpCode: string): Promise<void> {
		code = otpCode;
		/** Bail out early if the user hasn't provided an email. */
		if (!email.trim()) {
			emailError = t2('Email is required.');
			return;
		}

		loading = true;
		errorMessage = '';
		/** Send the code to the server for validation. */
		try {
			await validateResetCode(code);
			codeValidated = true;
		} catch (err: any) {
			errorMessage = err?.message || t2('Invalid or expired code.');
		} finally {
			loading = false;
		}
	}

	/**
	 * Submit the new password.
	 */
	async function handleResetPassword(): Promise<void> {
		if (!validatePasswordForm()) return;

		loading = true;
		/** Submit the new password to the server. */
		try {
			await resetPassword(code, newPassword);
			success = true;
		} catch (err: any) {
			errorMessage = err?.message || t2('Failed to reset password. Please try again.');
		} finally {
			loading = false;
		}
	}

	/** Handle Enter key on the password form. */
	function handleKeypress(event: KeyboardEvent): void {
		if (event.key === 'Enter') handleResetPassword();
	}
	let params = $derived(parseQuery($querystring));
	let emailFromUrl = $derived(params.get('email') || '');
	let codeFromUrl = $derived(params.get('code') || '');
	// ── Initialise from URL ───────────────────────────────────

	run(() => {
		if (emailFromUrl && !email) email = emailFromUrl;
	});
	run(() => {
		if (codeFromUrl && !code) {
			code = codeFromUrl;
			/* If both email and code came from URL, auto-validate. */
			if (emailFromUrl) autoValidate();
		}
	});
</script>

<AuthLayout
	heading={$t("Reset password")}
	subheading={codeValidated ? t2('Choose a new password.') : t2('Enter the code sent to your email.')}
>
	{#if loading}
		<LoadingOverlay />
	{/if}

	{#if errorMessage}
		<AlertMessage type="error" message={errorMessage} onclose={() => (errorMessage = '')} />
	{/if}

	{#if success}
		<!-- Success state -->
		<AlertMessage type="success" message={$t("Your password has been reset successfully.")} dismissible={false} />
		<div class="mt-6 text-center">
			<RoutedButton href="/login">{$t("Sign in")}</RoutedButton>
		</div>
	{:else if !codeValidated}
		<!-- Step 1: Enter email + code -->
		<FormField
			id="reset-email"
			label={$t("Email")}
			type="email"
			placeholder={$t("you@example.com")}
			bind:value={email}
			error={emailError}
			disabled={!!emailFromUrl}
			required
		/>

		<p class="mb-2 text-sm text-gray-500 dark:text-gray-400">{$t("Enter the 6-digit code from your email:")}</p>
		<OTPInput oncomplete={handleCodeComplete} error={errorMessage ? ' ' : ''} />
	{:else}
		<!-- Step 2: Set new password -->
		<form onsubmit={preventDefault(handleResetPassword)}>
			<PasswordField
				id="reset-new-password"
				label={$t("New password")}
				placeholder={$t("At least 8 characters")}
				bind:value={newPassword}
				error={passwordError}
				helper={$t("Must be at least 8 characters.")}
				required
				onkeypress={handleKeypress}
			/>

			<PasswordField
				id="reset-confirm-password"
				label={$t("Confirm new password")}
				placeholder={$t("Re-enter your new password")}
				bind:value={confirmPassword}
				error={confirmPasswordError}
				required
				onkeypress={handleKeypress}
			/>

			<Button type="submit" class="w-full" disabled={loading}>{$t("Reset password")}</Button>
		</form>
	{/if}

	<!-- Back to login -->
	<div class="mt-8 text-center text-sm text-gray-500 dark:text-gray-400">
		<RoutedLink to="/login">
			<span class="font-medium text-primary-600 hover:underline dark:text-primary-400">{$t("Back to sign in")}</span>
		</RoutedLink>
	</div>
</AuthLayout>
